/**
 * MovieConstellationBuilder Component
 * Manages the collection of movie influence nodes with add/remove functionality
 */

import { useState } from 'react';
import { useMovieExploreStore } from '../../stores/movie-explore-store';
import MovieNodeChip from './MovieNodeChip';
import type { MovieNodeType } from '../../types/movie-explore.types';

const NODE_TYPES: { value: MovieNodeType; label: string }[] = [
  { value: 'director', label: 'Director' },
  { value: 'genre', label: 'Genre' },
  { value: 'mood', label: 'Mood' },
  { value: 'cinematographer', label: 'Cinematographer' },
  { value: 'country', label: 'Country' },
  { value: 'era', label: 'Era' },
  { value: 'movement', label: 'Movement' },
  { value: 'theme', label: 'Theme' },
  { value: 'actor', label: 'Actor' },
  { value: 'studio', label: 'Studio / Distributor' },
];

export default function MovieConstellationBuilder() {
  const { nodes, savedConstellations, addNode, removeNode, updateNodeWeight, loadConstellation } =
    useMovieExploreStore();

  const [selectedType, setSelectedType] = useState<MovieNodeType>('director');
  const [nodeValue, setNodeValue] = useState('');
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);

  const handleAddNode = (e: React.FormEvent) => {
    e.preventDefault();
    if (nodeValue.trim()) {
      addNode({
        type: selectedType,
        value: nodeValue.trim(),
        weight: 0.5,
      });
      setNodeValue('');
    }
  };

  const handleLoadConstellation = (id: string) => {
    const constellation = savedConstellations.find((c) => c.id === id);
    if (constellation) {
      loadConstellation(constellation);
      setShowSavedDropdown(false);
    }
  };

  const isValid = nodes.length >= 3;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Constellation Builder</h2>

      {/* Add Node Form */}
      <form onSubmit={handleAddNode} className="mb-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as MovieNodeType)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0"
          >
            {NODE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={nodeValue}
            onChange={(e) => setNodeValue(e.target.value)}
            placeholder="Enter value..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0"
          />
          <button
            type="submit"
            disabled={!nodeValue.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            + Add
          </button>
        </div>
      </form>

      {/* Validation Message */}
      {nodes.length > 0 && !isValid && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm">
          ⚠️ Add at least {3 - nodes.length} more node{3 - nodes.length > 1 ? 's' : ''} to
          generate (minimum 3 required)
        </div>
      )}

      {/* Node List */}
      <div className="space-y-2 mb-4">
        {nodes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="mb-2">No nodes yet. Add at least 3 to start exploring.</p>
            <p className="text-sm">
              Try: <span className="font-medium">Chantal Akerman (director)</span> +{' '}
              <span className="font-medium">Belgium (country)</span> +{' '}
              <span className="font-medium">Feminist cinema (movement)</span>
            </p>
          </div>
        ) : (
          nodes.map((node, index) => (
            <MovieNodeChip
              key={`${node.type}-${node.value}-${index}`}
              node={node}
              index={index}
              onRemove={removeNode}
              onWeightChange={updateNodeWeight}
            />
          ))
        )}
      </div>

      {/* Saved Constellations Dropdown */}
      {savedConstellations.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Load Saved Constellation
          </label>
          <div className="relative">
            <button
              onClick={() => setShowSavedDropdown(!showSavedDropdown)}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-left hover:bg-gray-100 transition-colors"
            >
              Select constellation...
            </button>
            {showSavedDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {savedConstellations.map((constellation) => (
                  <button
                    key={constellation.id}
                    onClick={() => handleLoadConstellation(constellation.id)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors"
                  >
                    <div className="font-medium text-gray-900">{constellation.name}</div>
                    {constellation.description && (
                      <div className="text-sm text-gray-500">{constellation.description}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {constellation.nodes.length} nodes
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Node Count */}
      {nodes.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          {nodes.length} / 20 nodes{' '}
          {isValid && <span className="text-green-600 font-medium">✓ Ready to generate</span>}
        </div>
      )}
    </div>
  );
}
